import { useState } from "react";
import { Affiliation } from "@max/common/signup/types";
import { Layout, useSignupContext } from "Components";
import { styled } from "styled-components";
import { Body1, H4 } from "melodies-source/Text";
import { Select } from "melodies-source/Select";
import { Modal } from "melodies-source/Modal";
import { TwoColumns } from "Components/TwoColumns";
import { Button } from "melodies-source/Button";
import { ReactComponent as HeadingIcon } from "assets/svg/your-affiliation.svg";
import { ReactComponent as ArrowRightIcon } from "assets/svg/arrow-right.svg";

export const AFFILIATION_OPTIONS: { label: string; value: Affiliation }[] = [
  { label: "I'm the Artist", value: "artist" },
  { label: "Artist's Management Company", value: "management-company" },
  { label: "Artist's Marketing/Consulting Firm", value: "marketing" },
  { label: "Artist's Music Label", value: "label" },
  { label: "Artist's Tour Crew", value: "crew" },
  { label: "I'm a Fan", value: "fan" },
  { label: "Other", value: "other" },
];

export const ArtistAffiliation = () => {
  const { data, handleNext, handleBack } = useSignupContext();
  const [selectedAffiliation, setSelectedAffiliation] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const artistName = data.artistName || data.artistSearch;

  return (
    <Layout
      cardProps={{
        size: "large",
        heading: { icon: <HeadingIcon />, text: "Your Affiliation" },
        actions: {
          link: "contact",
          buttons:
            selectedAffiliation === "fan" ? undefined : (
              <>
                <Button variant="outlined" onClick={() => handleBack()}>
                  Back
                </Button>
                <Button
                  disabled={
                    !selectedAffiliation || selectedAffiliation === "fan"
                  }
                  rightIcon={<ArrowRightIcon />}
                  onClick={() =>
                    handleNext({ affiliation: selectedAffiliation })
                  }
                >
                  Next
                </Button>
              </>
            ),
        },
      }}
    >
      <Content>
        <div>
          <H4>What is your affiliation with {artistName}?</H4>
          <Body1>
            With so many people potentially involved in the success of this
            artist, we want to make sure that your SET experience is tailored to
            your role and that we are granting access to the right person.
            <br />
            <br />
            <LinkText onClick={() => setModalIsOpen(true)}>
              Not sure which to choose?
            </LinkText>
          </Body1>
        </div>
        <FlexColumn>
          <Select
            placeholder="Choose Affiliation..."
            value={selectedAffiliation}
            onChange={setSelectedAffiliation}
            label="Choose what best describes you:"
            options={AFFILIATION_OPTIONS}
          />
          {selectedAffiliation === "fan" && (
            <FanMessage artistName={artistName} />
          )}
        </FlexColumn>
      </Content>
      <Modal
        isOpen={modalIsOpen}
        header="Artist Affiliations"
        onClose={() => setModalIsOpen(false)}
        variant="medium"
      >
        <ModalContent>
          <H4>Artist</H4>
          <Body1>Choose this option if you are the music artist.</Body1>
          <H4>Management Company</H4>
          <Body1>
            Select this option if you're part of the artist's management company
            in any capacity.
          </Body1>
          <H4>Marketing/Consulting Firm</H4>
          <Body1>
            Choose this option if you work for a third-party marketing or
            consulting firm helping the artist, the artist's management team or
            the artist's label grow the artist's fan base or community.
          </Body1>
          <H4>Music Label</H4>
          <Body1>
            Select this option if you work for the label's marketing, social
            media, CRM team or any other capacity.
          </Body1>
          <H4>Tour Crew</H4>
          <Body1>
            Choose this if you're the tour manager or part of the crew on the
            road helping to make the tour happen.
          </Body1>
        </ModalContent>
      </Modal>
    </Layout>
  );
};

export const FanMessage = ({ artistName }: { artistName?: string }) => {
  const handleFan = () => {
    window.location.href = "https://set.live";
  };
  return (
    <FanMessageContainer>
      <Body1>Are you a fan checking into a show for {artistName}?</Body1>
      <Button variant="secondary" onClick={handleFan}>
        Check In Here
      </Button>
    </FanMessageContainer>
  );
};

const FanMessageContainer = styled.div`
  background-color: #f7fafc;
  border: 1px solid #e6e9eb;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  padding: 20px 24px 24px;
  gap: 12px;

  ${Body1} {
    align-self: center;
    color: #333;
    font-weight: 500;
    max-width: 80%;
    text-align: center;
  }
  ${({ theme }) => theme.media.mobile} {
    ${Body1} {
      max-width: 100%;
    }
  }
`;

const Content = styled(TwoColumns)`
  & > div {
    h4 {
      margin-bottom: 20px;
    }
  }
`;

const ModalContent = styled.div`
  ${H4} {
    margin-top: 20px;
  }
`;

const LinkText = styled.span`
  color: #00e;
  cursor: pointer;
  text-decoration: underline;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
